import { useState, useMemo, useEffect, useCallback } from "react";
import { SelectOption, DateRange } from "@veneer/core";
import moment from "moment";
import appointmentService from "../../services/agendaApi/appointment.service";
import { IPointOfSaleByCoordinatesDto } from "../../models/dto/agendaApi/IPointOfSaleDto";
import { AppointmentTypeEnum } from "../../models/enum/AgendaApi/AppointmentTypeEnum";
import { ICreateWeeklyAppointmentDto } from "../../models/input/agendaApi/ICreateAppoiment";

export type WeekDayKey =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

export const MOMENT_DAY_MAP: WeekDayKey[] = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

export const DISPLAY_ORDER: WeekDayKey[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

export interface DaySchedule {
  enabled: boolean;
  startTime: moment.Moment | null;
  endTime: moment.Moment | null;
}

const WEEKDAY_NAME_MAP: Record<WeekDayKey, string> = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

type SelectValue = number | string | boolean;

const buildEmptyDaySchedules = (): Record<WeekDayKey, DaySchedule> => ({
  MONDAY:    { enabled: false, startTime: null, endTime: null },
  TUESDAY:   { enabled: false, startTime: null, endTime: null },
  WEDNESDAY: { enabled: false, startTime: null, endTime: null },
  THURSDAY:  { enabled: false, startTime: null, endTime: null },
  FRIDAY:    { enabled: false, startTime: null, endTime: null },
  SATURDAY:  { enabled: false, startTime: null, endTime: null },
  SUNDAY:    { enabled: false, startTime: null, endTime: null },
});

export const WEEKLY_AGENDA_MAX_RANGE_DAYS = 6;
export const WEEKLY_AGENDA_MAX_START_MONTHS = 3;

const toIsoDateString = (value: DateRange["startDate"] | DateRange["endDate"]): string => {
  if (!value) return "";
  if (typeof (value as moment.Moment).format === "function") {
    const m = value as moment.Moment;
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  }
  const parsed = moment(String(value));
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const useWeeklyAgenda = () => {
  const typeOfVisitId = AppointmentTypeEnum.Visit;

  const [subRetail, setSubRetail] = useState<SelectValue[]>([]);
  const [pointOfSale, setPointOfSale] = useState<SelectValue[]>([]);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [daySchedules, setDaySchedules] = useState<Record<WeekDayKey, DaySchedule>>(
    buildEmptyDaySchedules
  );

  const [subRetailData, setSubRetailData] = useState<SelectOption[]>([]);
  const [subRetailDataCopy, setSubRetailDataCopy] = useState<SelectOption[]>([]);
  const [pointOfSaleData, setPointOfSaleData] = useState<SelectOption[]>([]);
  const [pointOfSaleDataCopy, setPointOfSaleDataCopy] = useState<SelectOption[]>([]);

  const [disableSubretail, setDisableSubretail] = useState<boolean>(true);
  const [disablePointOfSale, setDisablePointOfSale] = useState<boolean>(true);
  const [errorPos, setErrorPos] = useState<boolean>(false);
  const [errorDatePicker, setErrorDatePicker] = useState<boolean>(false);
  const [isValidCoordinates, setIsValidCoordinates] = useState<boolean>(false);

  const visibleWeekdays = useMemo<WeekDayKey[]>(() => {
    if (!startDate || !endDate) return [];
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) return [];

    const seen = new Set<WeekDayKey>();
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day") && seen.size < 7) {
      seen.add(MOMENT_DAY_MAP[cursor.day()]);
      cursor.add(1, "day");
    }
    return DISPLAY_ORDER.filter((d) => seen.has(d));
  }, [startDate, endDate]);

  useEffect(() => {
    setDaySchedules((prev) => {
      let changed = false;
      const updated = { ...prev };
      (Object.keys(updated) as WeekDayKey[]).forEach((day) => {
        if (!visibleWeekdays.includes(day) && updated[day].enabled) {
          updated[day] = { enabled: false, startTime: null, endTime: null };
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [visibleWeekdays]);

  const isSubmitDisabled = useMemo<boolean>(() => {
    if (pointOfSale.length === 0) return true;
    if (!startDate || !endDate) return true;
    if (visibleWeekdays.length === 0) return true;
    const activeDays = visibleWeekdays.filter((d) => daySchedules[d].enabled);
    if (activeDays.length === 0) return true;
    return activeDays.some((d) => {
      const { startTime: st, endTime: et } = daySchedules[d];
      if (!st || !et) return true;
      return moment(et).isSameOrBefore(moment(st), "minute");
    });
  }, [
    pointOfSale,
    startDate,
    endDate,
    visibleWeekdays,
    daySchedules,
  ]);


  const loadSubRetail = useCallback(() => {
    appointmentService
      .getSubretailList()
      .then((data: any[]) => {
        const options: SelectOption[] = data
          .filter((item) => item.fieldID !== 0)
          .map((item) => ({ value: item.fieldID, label: item.fieldValue }));
        setSubRetailData(options);
        setSubRetailDataCopy(options);
      })
      .finally(() => setDisableSubretail(false));
  }, []);

  const loadPointOfSale = useCallback((values: string) => {
    setDisablePointOfSale(true);
    appointmentService
      .getPosListBySubretailList(values)
      .then((data: any[]) => {
        const options: SelectOption[] = data
          .filter((item) => !!item.fieldID)
          .map((item) => ({ value: item.fieldID, label: item.fieldValue }));
        setPointOfSaleData(options);
        setPointOfSaleDataCopy(options);
      })
      .finally(() => setDisablePointOfSale(false));
  }, []);

  const validatePosCoordinates = useCallback((posCode: string) => {
    appointmentService
      .getPosCoordinates(posCode)
      .then((coords: IPointOfSaleByCoordinatesDto[]) => {
        const invalid = coords.some(
          (c) => c.latitude === null || c.longitude === null
        );
        if (invalid) {
          setErrorPos(true);
          setIsValidCoordinates(false);
        } else {
          setErrorPos(false);
          setIsValidCoordinates(true);
        }
      })
      .catch(() => {
        setErrorPos(true);
        setIsValidCoordinates(false);
      });
  }, []);

  const handleSubRetailChange = useCallback(
    (value: SelectValue) => {
      setSubRetail([value]);
      setPointOfSale([]);
      loadPointOfSale(String(value));
    },
    [loadPointOfSale]
  );

  const handleSubRetailChangeList = useCallback(
    (value: SelectValue) => {
      setSubRetail((prev) => {
        const copy = [...prev];
        const idx = copy.indexOf(value);
        if (idx > -1) copy.splice(idx, 1);
        else copy.push(value);
        loadPointOfSale(String(copy));
        return copy;
      });
    },
    [loadPointOfSale]
  );

  const handleSubRetailClear = useCallback(() => {
    setSubRetail([]);
    setPointOfSale([]);
    setDisablePointOfSale(true);
    setErrorPos(false);
    setIsValidCoordinates(false);
  }, []);

  const handlePointOfSaleChange = useCallback(
    (value: SelectValue) => {
      setPointOfSale([value]);
      validatePosCoordinates(String(value));
    },
    [validatePosCoordinates]
  );

  const handlePointOfSaleChangeList = useCallback((value: SelectValue) => {
    setPointOfSale((prev) => {
      const copy = [...prev];
      const idx = copy.indexOf(value);
      if (idx > -1) copy.splice(idx, 1);
      else copy.push(value);
      return copy;
    });
  }, []);

  const handlePointOfSaleClear = useCallback(() => {
    setPointOfSale([]);
    setIsValidCoordinates(false);
    setErrorPos(false);
  }, []);

  const handleChangeDateRange = useCallback((date: DateRange) => {
    const newStart = toIsoDateString(date.startDate);
    const newEnd = toIsoDateString(date.endDate);
    setStartDate(newStart);

    if (newStart && newEnd) {
      const maxEnd = moment(newStart).add(WEEKLY_AGENDA_MAX_RANGE_DAYS, "days");
      setEndDate(
        moment(newEnd).isAfter(maxEnd, "day") ? maxEnd.format("YYYY-MM-DD") : newEnd
      );
      return;
    }

    setEndDate(newEnd);
  }, []);

  const handleDayToggle = useCallback((day: WeekDayKey) => {
    setDaySchedules((prev) => {
      const current = prev[day];
      return {
        ...prev,
        [day]: {
          enabled: !current.enabled,
          startTime: current.enabled ? null : current.startTime,
          endTime: current.enabled ? null : current.endTime,
        },
      };
    });
  }, []);

  const handleDayStartTime = useCallback(
    (day: WeekDayKey, time: moment.Moment | null) => {
      setDaySchedules((prev) => {
        const current = prev[day];
        const newEnd =
          current.endTime &&
          time &&
          moment(time).isSameOrAfter(moment(current.endTime), "minute")
            ? null
            : current.endTime;
        return { ...prev, [day]: { ...current, startTime: time, endTime: newEnd } };
      });
    },
    []
  );

  const handleDayEndTime = useCallback(
    (day: WeekDayKey, time: moment.Moment | null) => {
      setDaySchedules((prev) => {
        const current = prev[day];
        if (
          time &&
          current.startTime &&
          moment(time).isSameOrBefore(moment(current.startTime), "minute")
        ) {
          return prev;
        }
        return { ...prev, [day]: { ...current, endTime: time } };
      });
    },
    []
  );

  const reset = useCallback(() => {
    setSubRetail([]);
    setPointOfSale([]);
    setStartDate("");
    setEndDate("");
    setDaySchedules(buildEmptyDaySchedules());
    setSubRetailData([]);
    setSubRetailDataCopy([]);
    setPointOfSaleData([]);
    setPointOfSaleDataCopy([]);
    setDisableSubretail(true);
    setDisablePointOfSale(true);
    setErrorPos(false);
    setErrorDatePicker(false);
    setIsValidCoordinates(false);
  }, []);

  const buildCreateWeeklyAppointmentPayload = useCallback(
    (userID: number): ICreateWeeklyAppointmentDto => {
      const formattedStartDate = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
      const formattedEndDate = endDate ? moment(endDate).format("YYYY-MM-DD") : "";

      const daySchedulesPayload = visibleWeekdays
        .filter((day) => daySchedules[day].enabled)
        .map((day) => ({
          nameDay: WEEKDAY_NAME_MAP[day],
          assignHourIn: moment(daySchedules[day].startTime).format("HH:mm"),
          assignHourOut: moment(daySchedules[day].endTime).format("HH:mm"),
        }));

      const enabledDays = new Set(daySchedulesPayload.map((s) => s.nameDay));

      return {
        userID,
        posCode: pointOfSale.map(String).join(","),
        dateStart: formattedStartDate,
        dateEnd: formattedEndDate,
        dateVisit: formattedStartDate,
        daySchedules: daySchedulesPayload,
        monday: enabledDays.has("Monday"),
        tuesday: enabledDays.has("Tuesday"),
        wednesday: enabledDays.has("Wednesday"),
        thursday: enabledDays.has("Thursday"),
        friday: enabledDays.has("Friday"),
        saturday: enabledDays.has("Saturday"),
        sunday: enabledDays.has("Sunday"),
        appointmentTypeID: typeOfVisitId,
        visitId: typeOfVisitId,
      };
    },
    [pointOfSale, startDate, endDate, visibleWeekdays, daySchedules, typeOfVisitId]
  );

  return {
    typeOfVisitId,
    subRetail,
    pointOfSale,
    startDate,
    endDate,
    daySchedules,
    subRetailData,
    setSubRetailData,
    subRetailDataCopy,
    pointOfSaleData,
    setPointOfSaleData,
    pointOfSaleDataCopy,
    disableSubretail,
    disablePointOfSale,
    errorPos,
    errorDatePicker,
    isValidCoordinates,
    visibleWeekdays,
    isSubmitDisabled,
    handleSubRetailChange,
    handleSubRetailChangeList,
    handleSubRetailClear,
    handlePointOfSaleChange,
    handlePointOfSaleChangeList,
    handlePointOfSaleClear,
    handleChangeDateRange,
    handleDayToggle,
    handleDayStartTime,
    handleDayEndTime,
    loadSubRetail,
    reset,
    buildCreateWeeklyAppointmentPayload,
  };
};

export default useWeeklyAgenda;
